import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { supabase } from "../supabaseClient";

const SavedJobsContext = createContext(null);

export function SavedJobsProvider({ children }) {
  const [savedJobIds, setSavedJobIds] = useState([]);
  const [userId, setUserId] = useState(null);
  const [loadingSavedJobs, setLoadingSavedJobs] = useState(true);

  const fetchSavedIds = useCallback(async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUserId(user?.id ?? null);

    if (!user) {
      setSavedJobIds([]);
      setLoadingSavedJobs(false);
      return;
    }

    const { data, error } = await supabase
      .from("saved_jobs")
      .select("job_id")
      .eq("user_id", user.id);

    if (error) {
      console.error("Error fetching saved jobs:", error.message);
      setSavedJobIds([]);
    } else {
      setSavedJobIds((data || []).map((r) => r.job_id));
    }
    setLoadingSavedJobs(false);
  }, []);

  useEffect(() => {
    fetchSavedIds();
  }, [fetchSavedIds]);

  const refreshSavedJobs = useCallback(() => {
    setLoadingSavedJobs(true);
    return fetchSavedIds();
  }, [fetchSavedIds]);

  const toggleSave = useCallback(
    async (jobId) => {
      if (!userId) {
        return { ok: false, reason: "auth" };
      }

      const saved = savedJobIds.includes(jobId);
      if (saved) {
        const { error } = await supabase
          .from("saved_jobs")
          .delete()
          .eq("user_id", userId)
          .eq("job_id", jobId);
        if (!error) {
          setSavedJobIds((prev) => prev.filter((id) => id !== jobId));
        }
        return { ok: !error };
      }

      const { error } = await supabase
        .from("saved_jobs")
        .insert([{ user_id: userId, job_id: jobId }]);
      const success = !error || error?.code === "23505";
      if (success) {
        setSavedJobIds((prev) => (prev.includes(jobId) ? prev : [...prev, jobId]));
      }
      return { ok: success };
    },
    [userId, savedJobIds]
  );

  const value = {
    savedJobIds,
    userId,
    loadingSavedJobs,
    toggleSave,
    refreshSavedJobs,
  };

  return (
    <SavedJobsContext.Provider value={value}>
      {children}
    </SavedJobsContext.Provider>
  );
}

export function useSavedJobs() {
  const context = useContext(SavedJobsContext);
  if (context == null) {
    throw new Error("useSavedJobs must be used within a SavedJobsProvider");
  }
  return context;
}
